import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Brand } from "../Brand";
import { Button } from "../ui/button";
import { useAuth } from "../../lib/auth";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import NotificationsBell from "./NotificationsBell";
import {
  LayoutDashboard, Receipt, Wallet, Settings as SettingsIcon, ShieldCheck,
  Plus, LogOut, UserCircle, Menu, X, FileCheck2,
} from "lucide-react";

const BASE_NAV = [
  { to: "/account", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/account/orders", label: "Orders", icon: Receipt },
  { to: "/account/wallets", label: "Wallets", icon: Wallet },
  { to: "/account/kyc", label: "Verification", icon: ShieldCheck },
  { to: "/account/settings", label: "Settings", icon: SettingsIcon },
];

export function AccountGuard({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) {
    setTimeout(() => navigate("/login", { replace: true }), 0);
    return null;
  }
  return children;
}

export function AccountLayout({ children, title, subtitle, actions, onOpenBuy }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (item) => item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  // Insert SoF nav link if applicable
  const NAV = [...BASE_NAV];
  if (user?.sof_status && user.sof_status !== "not_required") {
    NAV.splice(4, 0, { to: "/account/sof", label: "Source of Funds", icon: FileCheck2 });
  }

  return (
    <div className="min-h-screen bg-background" data-testid="account-layout">
      <header className="xch-ink sticky top-0 z-30 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="account-home-btn">
            <Brand size={28} theme="dark" />
          </button>
          <div className="flex items-center gap-2">
            <Button
              onClick={onOpenBuy || (() => navigate("/?buy=1"))}
              size="sm"
              className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
              data-testid="account-buy-btn"
            >
              <Plus className="w-4 h-4" /> Buy
            </Button>
            <NotificationsBell />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 text-white hover:text-white hover:bg-white/10 rounded-xl" data-testid="account-user-menu">
                  <UserCircle className="w-4 h-4" />
                  <span className="hidden sm:inline">{user?.name?.split(" ")[0] || "Account"}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-52 rounded-xl">
                <div className="px-2 py-1.5 border-b border-slate-100 mb-1.5">
                  <div className="font-display font-semibold text-sm text-foreground truncate">{user?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                </div>
                <button onClick={() => { logout(); navigate("/"); }}
                        className="w-full text-left text-sm px-2 py-1.5 hover:bg-muted rounded-md text-destructive flex items-center gap-1.5"
                        data-testid="account-logout-btn">
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </PopoverContent>
            </Popover>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden text-white p-2 rounded-lg hover:bg-white/10"
              aria-label="Menu"
              data-testid="account-mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-10 grid md:grid-cols-[220px_1fr] gap-6 md:gap-10">
        {/* Sidebar */}
        <aside className={`${mobileOpen ? "block" : "hidden"} md:block`} data-testid="account-sidebar">
          <nav className="space-y-0.5 md:sticky md:top-24">
            {NAV.map((item) => (
              <button
                key={item.to}
                onClick={() => { navigate(item.to); setMobileOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                  isActive(item)
                    ? "bg-primary text-white"
                    : "text-foreground/70 hover:text-foreground hover:bg-muted"
                }`}
                data-testid={`account-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="min-w-0" data-testid="account-content">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <h1 className="xch-headline text-3xl sm:text-4xl">{title}</h1>
              {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
            </div>
            {actions}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}
